import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { mspApi } from '../services/api';
import { getSelectedTenantId, setSelectedTenantId } from '../services/tenantStorage';

const TenantContext = createContext(null);

const normalizeTenants = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.tenants)) return payload.tenants;
  return [];
};

export function TenantProvider({ children, isAuthenticated, accountType }) {
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantIdState] = useState(getSelectedTenantId());

  const updateSelectedTenantId = useCallback((tenantId) => {
    setSelectedTenantIdState(tenantId);
    setSelectedTenantId(tenantId);
  }, []);

  useEffect(() => {
    let isMounted = true;

    if (isAuthenticated && accountType === 'msp') {
      mspApi
        .listTenants()
        .then((response) => {
          if (!isMounted) return;
          const tenantList = normalizeTenants(response.data);
          setTenants(tenantList);
          const hasSelectedTenant = tenantList.some(
            (tenant) => (tenant.id || tenant.tenant_id) === selectedTenantId
          );
          if (tenantList.length > 0 && (!selectedTenantId || !hasSelectedTenant)) {
            const defaultTenant = tenantList[0];
            const defaultId = defaultTenant.id || defaultTenant.tenant_id || '';
            if (defaultId) {
              updateSelectedTenantId(defaultId);
            }
          }
        })
        .catch(() => {
          if (isMounted) {
            setTenants([]);
          }
        });
    } else {
      setTenants([]);
      updateSelectedTenantId('');
    }

    return () => {
      isMounted = false;
    };
  }, [accountType, isAuthenticated, selectedTenantId, updateSelectedTenantId]);

  useEffect(() => {
    const storedTenantId = getSelectedTenantId();
    if (storedTenantId !== selectedTenantId) {
      setSelectedTenantIdState(storedTenantId);
    }
  }, [selectedTenantId]);

  const value = {
    tenants,
    selectedTenantId,
    setSelectedTenantId: updateSelectedTenantId,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export const useTenantContext = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenantContext must be used within TenantProvider');
  }
  return context;
};
